import { resources } from "@constants/hardcoded-strings";
import { DEFAULT_PAGE_SIZE } from "@constants/table-defaults";
import { IdwTableContainer } from "@containers/common/idw-table-container/idw-table-container";
import { IdwTable } from "@containers/common/idw-table/idw-table";
import {
    AssertionScoreStatus
} from "@enums/assertion-score-status-types";

import { SelectOption } from "@idw-types/common";
import { Assertion, AssertionScoreAssertionStatusValueMap, AvailableAssertionStatus, AvailableScoreValue, TrustScoreAssertionStatusValueMap } from "@idw-types/data/reference-data/generic";
import { FormattedRpServiceConfigurationServiceOffering } from "@idw-types/data/rp-service-configuration";
import { FormattedRpServiceConfigurationScoreModifier } from "@idw-types/data/rp-service-configuration/rp-service-configuration-score";
import { ScoreStatusType } from "@idw-types/data/rp-service-configuration/scores/score-status-type";
import { ScoreValueProperty } from "@idw-types/data/rp-service-configuration/scores/score-value-property";
import { getAvailableAssertionStatusMap } from "@utils/scores/score-util";
import { useMemo } from "react";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { ScoreTableDynamicCell } from "../score-table-dynamic-cell/score-table-dynamic-cell";
import { AccessorKeyColumnDef, CellContext, createColumnHelper, getCoreRowModel, useReactTable } from "@tanstack/react-table";

type ScoreModifiersTableContainerProps = {
    isDisabled: boolean;
    scoreModifiers: Array<FormattedRpServiceConfigurationScoreModifier>;
    canViewPassScoreModifiers: boolean;
    canViewCloseScoreModifiers: boolean;
    canViewPartialScoreModifiers: boolean;
    canViewUnverifiedScoreModifiers: boolean;
    canViewWarnScoreModifiers: boolean;
    canViewFailScoreModifiers: boolean;
    assertions: Array<Assertion>;
    availableAssertionStatuses: Array<AvailableAssertionStatus>;
    availableScoreValues: Array<AvailableScoreValue>;
    scoreAssertionStatusValueMaps: Array<AssertionScoreAssertionStatusValueMap | TrustScoreAssertionStatusValueMap>;
    formattedServiceOfferings: Array<
        FormattedRpServiceConfigurationServiceOffering
    >;
    setScoreModifiersState: (
        scoreModifiers: Array<FormattedRpServiceConfigurationScoreModifier>
    ) => void;
};


export type ScoreModifierTableDataRow = FormattedRpServiceConfigurationScoreModifier & {
    assertionName: string;
    serviceOfferingName: string;
};

export const ScoreModifiersTableContainer = (
    props: ScoreModifiersTableContainerProps
) => {
    const {
        isDisabled,
        scoreModifiers,
        canViewPassScoreModifiers,
        canViewCloseScoreModifiers,
        canViewPartialScoreModifiers,
        canViewUnverifiedScoreModifiers,
        canViewWarnScoreModifiers,
        canViewFailScoreModifiers,
        assertions,
        formattedServiceOfferings,
        availableAssertionStatuses,
        availableScoreValues,
        scoreAssertionStatusValueMaps,
        setScoreModifiersState,
    } = props;

    const formattedServiceOfferingMap = useMemo(() => {
        return formattedServiceOfferings.reduce(
            (result, formattedServiceOffering) => {
                result[
                    formattedServiceOffering.serviceOfferingID
                ] = formattedServiceOffering;
                return result;
            },
            {} as { [id in string]: FormattedRpServiceConfigurationServiceOffering }
        );
    }, [formattedServiceOfferings]);

    const scoreModifiersTableData: Array<ScoreModifierTableDataRow> = useMemo(() => {
        return scoreModifiers.map((asm) => ({
            ...asm,
            assertionName: assertions.find(
                (assertion) => assertion.id === asm.assertionID
            )?.shortName,
            serviceOfferingName: asm.serviceOfferingIDs
                .map(
                    (serviceOfferingID) =>
                        formattedServiceOfferingMap[serviceOfferingID].name
                )
                .join(" "),
        }));
    }, [scoreModifiers, formattedServiceOfferingMap, assertions]);

    const handleAssertionScoreValueChange = (
        option: SelectOption,
        rowIndex: number,
        property: ScoreValueProperty
    ) => {

        const finalScoreModifiers = scoreModifiers.map((asm, index) => {
            if (rowIndex === index) {
                return {
                    ...asm,
                    [property]: option.value
                }
            }
            return asm;
        });

        setScoreModifiersState(finalScoreModifiers);
    };

    const assertionStatusMap = useMemo(() => {
        return getAvailableAssertionStatusMap(availableAssertionStatuses, availableScoreValues, scoreAssertionStatusValueMaps);
    }, [availableAssertionStatuses, availableScoreValues, scoreAssertionStatusValueMaps]);

    const addDynamicColumn = (id: ScoreStatusType) => {
        const label =
            resources.pages.verificationServiceDetails.tabs.scores.table.columns[id]
                .header;
        const propertyName: ScoreValueProperty = `${id}Score`;

        return columnHelper.accessor(propertyName, {
            header: label,
            cell: (props) => {
                return <ScoreTableDynamicCell
                    isDisabled={isDisabled}
                    data={props.row.original}
                    rowIndex={props.row.index}
                    id={id}
                    label={label}
                    propertyName={propertyName}
                    assertionStatusMap={assertionStatusMap}
                    handleAssertionScoreValueChange={handleAssertionScoreValueChange}
                />
            }
        });
    };

    const getBasicDynamicCell = (
        props: CellContext<ScoreModifierTableDataRow, string>,
        propertyName: 'assertionName' | 'serviceOfferingName'
    ) => {
        const text = props.row.original[propertyName];
        const renderTooltip = (props: any) => {
            return <Tooltip {...props}>{text}</Tooltip>;
        };

        return (
            <OverlayTrigger overlay={renderTooltip}>
                <span>{text}</span>
            </OverlayTrigger>
        );
    }

    const getAssertionScoreValueDynamicColumns = () => {
        const columns: Array<AccessorKeyColumnDef<ScoreModifierTableDataRow, string>> = [];
        const assertionScoreTypes = [
            {
                id: AssertionScoreStatus.PASS,
                visible: canViewPassScoreModifiers,
            },
            {
                id: AssertionScoreStatus.CLOSE,
                visible: canViewCloseScoreModifiers,
            },
            {
                id: AssertionScoreStatus.PARTIAL,
                visible: canViewPartialScoreModifiers,
            },
            {
                id: AssertionScoreStatus.UNVERIFIED,
                visible: canViewUnverifiedScoreModifiers,
            },
            {
                id: AssertionScoreStatus.FAIL,
                visible: canViewFailScoreModifiers,
            },
            {
                id: AssertionScoreStatus.WARN,
                visible: canViewWarnScoreModifiers,
            },
        ];

        assertionScoreTypes.forEach((assertionScoreType) => {
            const { id, visible } = assertionScoreType;

            if (visible) {
                columns.push(addDynamicColumn(id));
            }
        });

        return columns;
    };

    const columnHelper = createColumnHelper<ScoreModifierTableDataRow>();

    const columns = useMemo(() => {
        const finalColumns = [
            columnHelper.accessor("assertionName", {
                header: resources.pages.verificationServiceDetails.tabs.scores.table.columns.assertion.header,
                cell: (props) => getBasicDynamicCell(props, 'assertionName')
            }),
            columnHelper.accessor("serviceOfferingName", {
                header: resources.pages.verificationServiceDetails.tabs.scores.table.columns.serviceOffering.header,
                cell: (props) => getBasicDynamicCell(props, 'serviceOfferingName')
            }),
            ...getAssertionScoreValueDynamicColumns()
        ];

        return finalColumns;
    }, [
        scoreModifiersTableData,
        scoreModifiers,
        canViewPassScoreModifiers,
        canViewCloseScoreModifiers,
        canViewPartialScoreModifiers,
        canViewUnverifiedScoreModifiers,
        canViewFailScoreModifiers,
        isDisabled
    ]);

    const useTableResult = useReactTable({
        columns,
        data: scoreModifiersTableData,
        filterFns: null,
        enableSorting: false,
        enableFilters: false,
        sortDescFirst: false,
        initialState: {
            pagination: {
                pageIndex: 0,
                pageSize: DEFAULT_PAGE_SIZE,
            }
        },
        getCoreRowModel: getCoreRowModel()
    });

    return (
        <IdwTableContainer>
            <IdwTable
                id="score-table"
                useTableResult={useTableResult}
                headerColumnKeyPrefix={"score-table"}
            />
        </IdwTableContainer>
    );
};
